import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ViewWrapper from "./ViewWrapper";

// ─── Dukkana Architecture Diagram ────────────────────────────────────────────
const DukkanaArchDiagram = () => {
  const storefronts = [
    {
      name: "Bfly Beauty",
      url: "http://bfly-beauty.com",
      logo: "http://bfly-beauty.com/logo.png",
      color: "#f472b6",
    },
    {
      name: "Montevion",
      url: "http://montevion.net",
      logo: "http://montevion.net/logo.png",
      color: "#a78bfa",
    },
    {
      name: "Bait Alhekma",
      url: "http://bait-alhekma.com",
      logo: "http://bait-alhekma.com/logo.png",
      color: "#34d399",
    },
    {
      name: "Alsham Commerce",
      url: "https://alshamcommerce.com",
      logo: "https://alshamcommerce.com/logo.png",
      color: "#60a5fa",
    },
  ];

  const [hoveredNode, setHoveredNode] = useState(null);
  const [imgErrors, setImgErrors] = useState({});

  return (
    <div style={{ width: "100%", fontFamily: "'DM Mono', monospace" }}>
      {/* Header */}
      <div
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          paddingBottom: "16px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            fontSize: "0.65rem",
            color: "#00ff88",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginBottom: "6px",
          }}
        >
          System Architecture
        </div>
        <div
          style={{
            fontSize: "1rem",
            fontWeight: 700,
            color: "#e8edf3",
            fontFamily: "'Syne', sans-serif",
          }}
        >
          Dukkana — Multi-Tenant SaaS Engine
        </div>
        <div
          style={{ fontSize: "0.72rem", color: "#6b7889", marginTop: "4px" }}
        >
          One engine. Many storefronts. Full isolation per tenant.
        </div>
      </div>

      {/* Live Storefronts */}
      <div style={{ marginBottom: "28px" }}>
        <div
          style={{
            fontSize: "0.62rem",
            color: "#6b7889",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            marginBottom: "12px",
          }}
        >
          Live Partner Storefronts
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "10px",
          }}
        >
          {storefronts.map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
                padding: "12px 8px",
                background: "rgba(255,255,255,0.03)",
                border: `1px solid ${s.color}33`,
                borderRadius: "8px",
                textDecoration: "none",
                transition: "all 0.2s",
                cursor: "pointer",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = `${s.color}10`)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.03)")
              }
            >
              {!imgErrors[s.name] ? (
                <img
                  src={s.logo}
                  alt={s.name}
                  style={{
                    width: "40px",
                    height: "40px",
                    objectFit: "contain",
                    borderRadius: "6px",
                  }}
                  onError={() =>
                    setImgErrors((prev) => ({ ...prev, [s.name]: true }))
                  }
                />
              ) : (
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "6px",
                    background: `${s.color}22`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.7rem",
                    color: s.color,
                    fontWeight: 700,
                  }}
                >
                  {s.name.slice(0, 2).toUpperCase()}
                </div>
              )}
              <span
                style={{
                  fontSize: "0.6rem",
                  color: "#9ca3af",
                  textAlign: "center",
                  lineHeight: 1.3,
                }}
              >
                {s.name}
              </span>
              <span style={{ fontSize: "0.55rem", color: s.color }}>
                Live ↗
              </span>
            </a>
          ))}
        </div>
      </div>

      {/* Architecture Layers */}
      <div style={{ marginBottom: "28px" }}>
        <div
          style={{
            fontSize: "0.62rem",
            color: "#6b7889",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            marginBottom: "12px",
          }}
        >
          System Layers
        </div>

        {/* Layer: Client Layer */}
        <ArchLayer
          label="Client Layer"
          color="#60a5fa"
          items={[
            {
              icon: "🛍️",
              label: "Custom Storefront UI",
              sub: "Next.js App Router + TypeScript",
            },
            {
              icon: "⚙️",
              label: "Admin Dashboard",
              sub: "Per-tenant controls & catalog",
            },
            {
              icon: "📦",
              label: "Order Tracking",
              sub: "Real-time status + history",
            },
          ]}
        />

        <FlowArrow label="HTTPS / REST" />

        {/* Layer: API Layer */}
        <ArchLayer
          label="API & Routing Layer"
          color="#a78bfa"
          items={[
            {
              icon: "🔀",
              label: "Tenant Router",
              sub: "Subdomain → tenant context",
            },
            {
              icon: "🔐",
              label: "Auth Middleware",
              sub: "Session + role guard",
            },
            {
              icon: "📡",
              label: "Express.js API",
              sub: "RESTful + business logic",
            },
          ]}
        />

        <FlowArrow label="Internal" />

        {/* Layer: Business Logic */}
        <ArchLayer
          label="Business Logic"
          color="#34d399"
          items={[
            {
              icon: "💳",
              label: "PayPal Integration",
              sub: "Checkout, captures, refunds",
            },
            {
              icon: "🏪",
              label: "Multi-Tenant Engine",
              sub: "Isolated catalog per tenant",
            },
            {
              icon: "📋",
              label: "Order Lifecycle",
              sub: "Placed → fulfilled → refunded",
            },
            {
              icon: "🤝",
              label: "Partner Onboarding",
              sub: "Merchant setup + compliance",
            },
          ]}
          columns={4}
        />

        <FlowArrow label="DB Query / File I/O" />

        {/* Layer: Infra */}
        <ArchLayer
          label="Infrastructure"
          color="#f97316"
          items={[
            {
              icon: "🐧",
              label: "Linux Server",
              sub: "UFW · iptables · hardened",
            },
            {
              icon: "🔒",
              label: "SSL/TLS + DNS",
              sub: "Let's Encrypt + Nginx proxy",
            },
            {
              icon: "🚀",
              label: "CI/CD Pipeline",
              sub: "Cryptographic deploy on commit",
            },
            {
              icon: "🗄️",
              label: "Database",
              sub: "Persistent, tenant-isolated",
            },
          ]}
          columns={4}
        />
      </div>

      {/* PayPal Partner callout */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "14px 16px",
          background: "rgba(0, 112, 186, 0.08)",
          border: "1px solid rgba(0, 112, 186, 0.25)",
          borderRadius: "8px",
          marginBottom: "16px",
        }}
      >
        <div style={{ fontSize: "1.1rem" }}>💳</div>
        <div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "#60a5fa",
              fontWeight: 600,
              marginBottom: "2px",
            }}
          >
            Verified PayPal Partner
          </div>
          <div
            style={{ fontSize: "0.68rem", color: "#6b7889", lineHeight: 1.5 }}
          >
            Built the full checkout flow, captures, refunds, and "paid but
            out-of-stock" resolution logic. Handles partner fee customization
          </div>
        </div>
      </div>

      {/* Links row */}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {[
          {
            label: "Dukkana Platform ↗",
            href: "https://dukkana.store/index.html",
          },
          { label: "Bfly Beauty ↗", href: "http://bfly-beauty.com" },
          { label: "Montevion ↗", href: "http://montevion.net" },
          { label: "Bait Alhekma ↗", href: "http://bait-alhekma.com" },
          { label: "Alsham Commerce ↗", href: "https://alshamcommerce.com" },
        ].map((link) => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              fontSize: "0.68rem",
              padding: "6px 14px",
              background: "rgba(0,255,136,0.06)",
              border: "1px solid rgba(0,255,136,0.2)",
              borderRadius: "20px",
              color: "#00ff88",
              textDecoration: "none",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(0,255,136,0.12)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(0,255,136,0.06)")
            }
          >
            {link.label}
          </a>
        ))}
      </div>
    </div>
  );
};

const ArchLayer = ({ label, color, items, columns }) => (
  <div
    style={{
      border: `1px solid ${color}22`,
      borderRadius: "8px",
      padding: "14px",
      background: `${color}07`,
      marginBottom: "2px",
    }}
  >
    <div
      style={{
        fontSize: "0.58rem",
        color: color,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        marginBottom: "10px",
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns || 3}, 1fr)`,
        gap: "8px",
      }}
    >
      {items.map((item) => (
        <div
          key={item.label}
          style={{
            padding: "10px",
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${color}1a`,
            borderRadius: "6px",
          }}
        >
          <div style={{ fontSize: "0.85rem", marginBottom: "4px" }}>
            {item.icon}
          </div>
          <div
            style={{
              fontSize: "0.7rem",
              color: "#e8edf3",
              fontWeight: 600,
              marginBottom: "2px",
              lineHeight: 1.3,
            }}
          >
            {item.label}
          </div>
          <div
            style={{ fontSize: "0.62rem", color: "#6b7889", lineHeight: 1.4 }}
          >
            {item.sub}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const FlowArrow = ({ label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: "4px 0",
      color: "#374151",
      fontSize: "0.6rem",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    }}
  >
    <div
      style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.06)" }}
    />
    <span style={{ color: "#4b5563" }}>{label}</span>
    <span style={{ color: "#4b5563", fontSize: "0.8rem" }}>↓</span>
    <div
      style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.06)" }}
    />
  </div>
);

// ─── Data ─────────────────────────────────────────────────────────────────────
const projectsData = [
  {
    name: "Dukkana — Multi-Tenant SaaS E-Commerce",
    category: "SaaS / Full-Stack",
    year: "2024 – Present",
    status: "Live in Production",
    statusType: "live",
    featured: true,
    description:
      "Founded and architected a production-grade, multi-tenant SaaS e-commerce engine. Each tenant gets a custom storefront, catalog, admin dashboard, and branding — all powered by a shared infrastructure I designed and maintain end-to-end.",
    bullets: [
      "Architected and shipped a production-grade, multi-tenant SaaS e-commerce engine (Next.js / Express.js) from the ground up",
      "Designed a scalable multi-tenancy system powering independent storefronts with a single engine and shared infrastructure, each with its own custom storefront, catalog, branding, admin dashboard, and operational controls",
      'Integrated PayPal\'s payment infrastructure as a verified PayPal Partner — built the full checkout flow, payment captures, refunds, and post-payment capture (resolving the "paid but out-of-stock" problem)',
      "Engineered and maintained the full Linux server infrastructure — provisioned and hardened the production environment, configured DNS, SSL/TLS, firewall rules (UFW/iptables), and built a cryptographic-principles CI/CD pipeline",
    ],
    tech: [
      "Next.js",
      "TypeScript",
      "Express.js",
      "Node.js",
      "PayPal API",
      "Linux",
      "SSL/TLS",
      "CI/CD",
    ],
    link: "https://dukkana.store/index.html",
  },
  {
    name: "Linux Kernel Stateful Firewall Module",
    category: "Systems / Cybersecurity",
    year: "2025",
    status: "Security Research",
    statusType: "research",
    description:
      "Engineered a high-grade stateful firewall as a Linux kernel module using Netfilter hooks — implementing the full TCP state machine and packet lifecycle from pre-routing through FIN_WAIT.",
    bullets: [
      "Full TCP state machine: SYN_SENT → ESTABLISHED → FIN_WAIT",
      "Built-in IPS, DLP, and MITM proxy for HTTP/FTP/SMTP traffic",
      "CVE-specific rules for Apache OFBiz vulnerabilities (CVE-2024-32113, CVE-2024-38856)",
      "Stateless and stateful packet inspection with Netfilter hooks",
    ],
    tech: [
      "C",
      "Linux Kernel",
      "Netfilter",
      "Iptables",
      "IPS",
      "DLP",
      "TCP/IP",
    ],
    link: "https://github.com/Tamerabdalrazaq/Linux-Stateful-Firewall",
  },
  {
    name: "Prosody Meter Classifier — Arabic NLP",
    category: "Machine Learning / NLP",
    year: "2022–2025",
    status: "Live Tool",
    statusType: "live",
    description:
      "An end-to-end NLP pipeline that takes raw, unvocalized Arabic text and classifies its poetic meter with 95% accuracy — a problem sitting at the intersection of linguistics, phonology, and machine learning.",
    bullets: [
      "Phonological extraction: built a custom text processing model to extract a binary tonal 'fingerprint' of each verse from raw, unvocalized script",
      "Dataset bootstrapping: developed a self-supervised method to generate a labeled training corpus from a large, previously unlabeled Arabic poetry collection",
      "Meter classification: trained a Random Forest classifier across all 16 classical meters, achieving ~95% accuracy",
      "Deployed as a live tool at aroody.net, actively used by researchers and Arabic poetry enthusiasts",
    ],
    tech: [
      "Python",
      "scikit-learn",
      "NLP",
      "Random Forest",
      "Phonology",
      "Self-Supervised Learning",
    ],
    link: "https://aroody.net",
  },
  {
    name: "Large-Scale Visual Analysis Research Collaboration",
    category: "Technical Research / Data Science",
    year: "2024–2025",
    status: "Peer-Reviewed Publication",
    statusType: "research",
    description:
      "Automated large-scale image processing pipelines for studying human visual attention patterns at Prof. Dominique Lamy's Lab, Tel Aviv University.",
    bullets: [
      "Processed and analysed datasets of 50,000+ records",
      "Custom mathematical metrics to quantify visual alignment",
      "R-based statistical analysis and Python processing pipelines",
      "Co-authoring upcoming peer-reviewed scientific publication",
    ],
    tech: [
      "Python",
      "R",
      "Data Pipelines",
      "Statistical Analysis",
      "Image Processing",
    ],
    link: "https://github.com/Tamerabdalrazaq/MCS-Daniel-Psy-Ws",
  },
  {
    name: "Algorithms Booklet",
    category: "Open Source / Education",
    year: "2024",
    status: "Open Source",
    statusType: "open",
    description:
      "Authored a comprehensive algorithms solutions booklet for TAU CS students preparing for exams with no official solution sets. Publicly available and widely used.",
    bullets: [
      "Covers core CS algorithm topics from TAU curriculum",
      "Helps hundreds of students with exam preparation",
      "Clear, pedagogically-structured worked solutions",
    ],
    tech: ["Algorithms", "CS Theory", "Open Source"],
    link: "https://drive.google.com/file/d/11Fu8tD3Bsaqa1JHgcHkCmNVaYVqfkz74/view?usp=sharing",
  },
];

// ─── Side / Misc Projects ─────────────────────────────────────────────────────
const sideProjectsData = [
  {
    name: "Prisoner's Dilemma — Ingroup Bias Experiment",
    category: "Experimental Psychology",
    year: "TAU",
    icon: "🧠",
    description:
      "Used the Prisoner's Dilemma and Dictator's Game to assess how ingroup bias affects cooperation. Collected data from ~100 students and drew conclusions with statistical analysis.",
    tech: ["React.js", "Experimental Design", "Statistics"],
    links: [
      {
        label: "Live Experiment",
        href: "https://tamerabdalrazaq.github.io/experiment",
      },
      {
        label: "Source",
        href: "https://github.com/Tamerabdalrazaq/experimental-psychology",
      },
    ],
  },
  {
    name: "React Chess Game",
    category: "Frontend / Game",
    year: "Side Project",
    icon: "♟️",
    description:
      "Fully functional chess game with complete movement rules, check/pin handling, highlighted legal moves, move history, piece cemetery, game modes, and per-player timers.",
    tech: ["React.js", "Game Logic", "UI/UX"],
    links: [
      {
        label: "Source",
        href: "https://github.com/Tamerabdalrazaq/Chess-Game-React",
      },
      {
        label: "Live Site",
        href: "https://mychessgame.netlify.app/",
      },
    ],
  },
  {
    name: "Rushd Center",
    category: "Full-Stack / EdTech",
    year: "Side Project",
    icon: "📚",
    description:
      "Full-stack learning platform for an educational collaboration center. Features a psychometric exam calculator, registration/contact forum, user authentication, exam grade history, and an interactive vocabulary section with a spaced-repetition memorization game.",
    tech: ["Full-Stack", "Authentication", "Spaced Repetition", "EdTech"],
    links: [
      {
        label: "Live Site",
        href: "https://rushd-center-tamerabdalrazaqs-projects.vercel.app/tasjeel",
      },
    ],
  },
  {
    name: "AVL Tree & Linked List",
    category: "Data Structures / Python",
    year: "Side Project",
    icon: "🌳",
    description:
      "Python implementations of AVL Tree and Linked List data structures with full unit test coverage. The AVL Tree is self-balancing with O(log n) height, maintaining balance through rotations on every insertion and deletion.",
    tech: ["Python", "Data Structures", "Unit Testing"],
    links: [
      { label: "Source", href: "https://github.com/Tamerabdalrazaq/AVL-LIST" },
    ],
  },
  {
    name: "Clustering Algorithm with C-Python API",
    category: "Algorithms / Systems",
    year: "Side Project",
    icon: "🔬",
    description:
      "Implements a clustering algorithm based on symmetric Non-negative Matrix Factorization (symNMF) via a C-Python API bridge. Applied to various datasets and benchmarked against the K-means clustering algorithm.",
    tech: ["Python", "C", "C-Python API", "Machine Learning", "Algorithms"],
    links: [
      {
        label: "Source",
        href: "https://github.com/Tamerabdalrazaq/C-Python-API-Clustering-Algorithm",
      },
    ],
  },
  {
    name: "Dual-Key Caesar Cipher",
    category: "Frontend / Cryptography",
    year: "Side Project",
    icon: "🔐",
    description:
      "A web implementation of the Dual-Key Caesar Cipher — supports both encoding and decoding with a clean, interactive UI.",
    tech: ["JavaScript", "HTML", "CSS", "Cryptography"],
    links: [
      { label: "Live Site", href: "https://dualkeycaesarcipher.netlify.app/" },
      {
        label: "Source",
        href: "https://github.com/Tamerabdalrazaq/Caisar-Cypher",
      },
    ],
  },
  {
    name: "2D Minecraft Game",
    category: "Frontend / Game",
    year: "Side Project",
    icon: "⛏️",
    description:
      "A 2D Minecraft-inspired game built with vanilla JS, HTML, and CSS. Responsive on load — the JS engine calculates layout dimensions at startup, so a page refresh is needed after resizing the browser window.",
    tech: ["Vanilla JS", "HTML", "CSS", "Game Dev"],
    links: [
      { label: "Live Game", href: "https://mycraft2d.netlify.app/" },
      {
        label: "Source",
        href: "https://github.com/Tamerabdalrazaq/Minecraft-Web-Project",
      },
    ],
  },
  {
    name: "Techno-Roll",
    category: "Full-Stack / E-Commerce",
    year: "Side Project",
    icon: "🛒",
    description:
      "A full-stack e-commerce website for a local engineering-solutions business. Includes a landing page, private admin panel for product management (add/remove/edit), product category pages, and a contact form linked directly to the store.",
    tech: ["Next.js", "JavaScript", "CSS", "E-Commerce"],
    links: [{ label: "Live Site", href: "https://techno-roll.com" }],
  },
  {
    name: "Restaurant Menu",
    category: "Frontend / Web",
    year: "Side Project",
    icon: "🍽️",
    description:
      "A digital menu website built",
    tech: ["Next.js", "JavaScript", "CSS"],
    links: [{ label: "Live Site", href: "http://menulee.vercel.app" }],
  },
];

const statusColors = {
  live: { color: "#00ff88", bg: "rgba(0,255,136,0.08)", label: "🟢" },
  research: { color: "#60a5fa", bg: "rgba(96,165,250,0.08)", label: "🔬" },
  open: { color: "#a78bfa", bg: "rgba(167,139,250,0.08)", label: "📖" },
};

// ─── Side Project Card ────────────────────────────────────────────────────────
const SideProjectCard = ({ proj, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: 0.08 * index }}
    style={{
      background: "#0a0e13",
      border: "1px solid rgba(255,255,255,0.05)",
      borderRadius: "8px",
      padding: "18px 20px",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    }}
  >
    {/* Top row */}
    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
      <span style={{ fontSize: "1rem", lineHeight: 1, paddingTop: "1px" }}>
        {proj.icon}
      </span>
      <div style={{ flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
            marginBottom: "4px",
          }}
        >
          <span
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "0.6rem",
              color: "#4b5563",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {proj.category}
          </span>
          <span
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "0.6rem",
              color: "#374151",
            }}
          >
            · {proj.year}
          </span>
        </div>
        <div
          style={{
            fontSize: "0.88rem",
            fontWeight: 700,
            color: "#c9d1da",
            lineHeight: 1.3,
            fontFamily: "'Syne', sans-serif",
          }}
        >
          {proj.name}
        </div>
      </div>
    </div>

    {/* Description */}
    <p
      style={{
        fontSize: "0.8rem",
        color: "#4b5563",
        lineHeight: 1.7,
        margin: 0,
      }}
    >
      {proj.description}
    </p>

    {/* Bottom row: tags + links */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "8px",
        marginTop: "2px",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
        {proj.tech.map((t) => (
          <span
            key={t}
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "0.58rem",
              padding: "2px 7px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.05)",
              borderRadius: "20px",
              color: "#374151",
            }}
          >
            {t}
          </span>
        ))}
      </div>
      {proj.links.length > 0 && (
        <div style={{ display: "flex", gap: "8px" }}>
          {proj.links.map((lnk) => (
            <a
              key={lnk.href}
              href={lnk.href}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "0.6rem",
                color: "#6b7889",
                textDecoration: "none",
                borderBottom: "1px solid rgba(107,120,137,0.3)",
                paddingBottom: "1px",
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#9ca3af")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#6b7889")}
            >
              {lnk.label} ↗
            </a>
          ))}
        </div>
      )}
    </div>
  </motion.div>
);

// ─── Component ────────────────────────────────────────────────────────────────
const Projects = () => {
  const [selected, setSelected] = useState(null);

  const featuredProject = projectsData[0];
  const restProjects = projectsData.slice(1);

  return (
    <ViewWrapper>
      <div
        style={{
          background: "#080c10",
          color: "#e8edf3",
          fontFamily: "'Syne', sans-serif",
          padding: "80px clamp(20px, 6vw, 100px)",
          display: "flex",
          flexDirection: "column",
          gap: "52px",
        }}
      >
        {/* Header */}
        <motion.div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "0.72rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#00ff88",
              display: "block",
              marginBottom: "10px",
            }}
          >
            Work
          </span>
          <h1
            style={{
              fontSize: "clamp(2rem, 5vw, 3.2rem)",
              fontWeight: 800,
              margin: "0 0 16px",
              letterSpacing: "-0.02em",
            }}
          >
            Selected Projects
          </h1>
          <p
            style={{
              fontSize: "0.97rem",
              color: "#6b7889",
              lineHeight: 1.8,
              maxWidth: "600px",
              margin: 0,
              textAlign: "center",
            }}
          >
            Production systems, security research, and open-source contributions
            — built end-to-end and shipped.
          </p>
        </motion.div>

        {/* ── Featured: Dukkana ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.15 }}
          style={{
            boxShadow: "0 0 18px rgba(0,255,136,0.08)",
          }}
        >
          {/* Featured badge label */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "#00ff88",
                // boxShadow: "0 0 8px #00ff88",
                boxShadow: "0 4px 100px rgba(255,255,255,0.6)",

                animation: "pulse 2s ease-in-out infinite",
              }}
            />
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "0.65rem",
                color: "#00ff88",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Featured — Flagship Project
            </span>
          </div>

          {/* Featured Card */}
          <div
            style={{
              background:
                "linear-gradient(135deg, #0d1a10 0%, #0a1118 40%, #0d1117 100%)",
              border: "1px solid rgba(0,255,136,0.3)",
              borderRadius: "14px",
              padding: "32px",
              cursor: "pointer",
              position: "relative",
              overflow: "hidden",
              boxShadow:
                "0 0 800px rgba(0,255,136,0.1), inset 0 1px 0 rgba(0,255,136,.8)",
              transition: "border-color 0.25s, box-shadow 0.25s",
            }}
            onClick={() =>
              setSelected(selected === "featured" ? null : "featured")
            }
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(0,255,136,0.5)";
              e.currentTarget.style.boxShadow =
                "0 0 60px rgba(0,255,136,0.09), inset 0 1px 0 rgba(0,255,136,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(0,255,136,0.3)";
              e.currentTarget.style.boxShadow =
                "0 0 800px rgba(0,255,136,0.1), inset 0 1px 0 rgba(0,255,136,.8)";
            }}
          >
            {/* Glow accent top-right */}
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: "200px",
                height: "200px",
                background:
                  "radial-gradient(circle at top right, rgba(0,255,136,0.07) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />

            {/* Top row */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "0.68rem",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#6b7889",
                  }}
                >
                  SaaS / Full-Stack
                </span>
                <span
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "0.68rem",
                    padding: "3px 10px",
                    borderRadius: "20px",
                    color: "#00ff88",
                    background: "rgba(0,255,136,0.08)",
                    fontWeight: 500,
                  }}
                >
                  🟢 Live in Production
                </span>
              </div>
              <span
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "0.7rem",
                  color: "#6b7889",
                }}
              >
                2024 – Present
              </span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "140px",
                height: "140px",
                margin: "auto",
                borderRadius: "50%",
                background:
                  "radial-gradient(circle at 30% 30%, #2a2a2a, #0a0a0a)",
                boxShadow:
                  "0 0 30px rgba(255, 215, 0, 0.15), 0 0 60px rgba(255, 215, 0, 0.05), inset 0 1px 1px rgba(255,255,255,0.08)",
                border: "1px solid rgba(255, 215, 0, 0.2)",
              }}
            >
              <img
                src="/dukkana.svg"
                alt="Dukkana"
                style={{
                  width: "70px",
                  height: "70px",
                  display: "block",
                  filter:
                    "invert(1) sepia(1) saturate(2) hue-rotate(5deg) brightness(1.4)",
                  opacity: 0.92,
                  dropShadow: "0 0 8px rgba(255,215,0,0.4)",
                }}
              />
            </div>

            {/* Title */}
            <h2
              style={{
                fontSize: "clamp(1.2rem, 2.5vw, 1.6rem)",
                fontWeight: 800,
                margin: "0 0 12px",
                color: "#e8edf3",
                letterSpacing: "-0.02em",
              }}
            >
              Dukkana — Multi-Tenant SaaS E-Commerce
            </h2>

            {/* Description */}
            <p
              style={{
                fontSize: "0.9rem",
                color: "#8b98a8",
                lineHeight: 1.75,
                margin: "0 0 18px",
                maxWidth: "780px",
              }}
            >
              Founded and architected a production-grade, multi-tenant SaaS
              e-commerce engine. Each
              tenant gets a custom storefront, catalog, admin dashboard, and
              branding — all powered by a shared infrastructure I designed and
              maintain end-to-end. From database schema to pixel-perfect UI,
              payment integrations, CI/CD pipelines, and hardened Linux server
              infrastructure — every layer is mine.
            </p>

            {/* Tags */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                marginBottom: "20px",
              }}
            >
              {featuredProject.tech.map((t) => (
                <span
                  key={t}
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "0.65rem",
                    padding: "3px 9px",
                    background: "rgba(0,255,136,0.05)",
                    border: "1px solid rgba(0,255,136,0.15)",
                    borderRadius: "20px",
                    color: "#6b9e7a",
                  }}
                >
                  {t}
                </span>
              ))}
            </div>

            {/* Expand hint */}
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "0.68rem",
                color: selected === "featured" ? "#00ff88" : "#6b7889",
                letterSpacing: "0.05em",
                transition: "color 0.2s",
              }}
            >
              {selected === "featured"
                ? "Hide architecture ↑"
                : "View architecture diagram →"}
            </div>

            {/* ── Expanded: Architecture Diagram ── */}
            <AnimatePresence>
              {selected === "featured" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4 }}
                  style={{ overflow: "hidden" }}
                >
                  <div
                    style={{
                      marginTop: "28px",
                      paddingTop: "28px",
                      borderTop: "1px solid rgba(255,255,255,0.07)",
                    }}
                  >
                    <DukkanaArchDiagram />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* ── Rest of projects grid ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: "16px",
            gridAutoRows: "max-content",
          }}
        >
          {restProjects.map((proj, i) => {
            const s = statusColors[proj.statusType];
            const key = `rest-${i}`;
            const isOpen = selected === key;
            return (
              <motion.div
                key={proj.name}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * i + 0.35 }}
                onClick={() => setSelected(isOpen ? null : key)}
                style={{
                  background: isOpen ? "#111821" : "#0d1117",
                  border: `1px solid ${isOpen ? "rgba(0,255,136,0.25)" : "rgba(255,255,255,0.07)"}`,
                  borderRadius: "10px",
                  padding: "28px",
                  cursor: "pointer",
                  transition: "border-color 0.25s, background 0.25s",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: isOpen
                    ? "0 0 18px rgba(0,255,136,0.08)"
                    : "0 4px 100px rgba(255,255,255,0.6)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(0,255,136,0.2)";
                  e.currentTarget.style.background = "#111821";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = isOpen
                    ? "rgba(0,255,136,0.25)"
                    : "rgba(255,255,255,0.07)";
                  e.currentTarget.style.background = isOpen
                    ? "#111821"
                    : "#0d1117";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "0.68rem",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: "#6b7889",
                      }}
                    >
                      {proj.category}
                    </span>
                    <span
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "0.68rem",
                        padding: "3px 10px",
                        borderRadius: "20px",
                        color: s.color,
                        background: s.bg,
                        fontWeight: 500,
                      }}
                    >
                      {s.label} {proj.status}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "10px",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "1.05rem",
                        fontWeight: 700,
                        margin: 0,
                        lineHeight: 1.3,
                        color: "#e8edf3",
                        flex: 1,
                      }}
                    >
                      {proj.name}
                    </h3>
                    <span
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "0.7rem",
                        color: "#6b7889",
                        whiteSpace: "nowrap",
                        paddingTop: "2px",
                      }}
                    >
                      {proj.year}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#6b7889",
                      lineHeight: 1.75,
                      margin: 0,
                    }}
                  >
                    {proj.description}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      marginTop: "4px",
                    }}
                  >
                    {proj.tech.map((t) => (
                      <span
                        key={t}
                        style={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: "0.65rem",
                          padding: "3px 9px",
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.07)",
                          borderRadius: "20px",
                          color: "#6b7889",
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.35 }}
                      style={{ overflow: "hidden" }}
                    >
                      <ul
                        style={{
                          margin: "20px 0 0",
                          padding: "20px 0 0",
                          borderTop: "1px solid rgba(255,255,255,0.07)",
                          listStyle: "none",
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        {proj.bullets.map((b) => (
                          <li
                            key={b}
                            style={{
                              fontSize: "0.875rem",
                              color: "#6b7889",
                              lineHeight: 1.7,
                              paddingLeft: "16px",
                              position: "relative",
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                left: 0,
                                color: "#00ff88",
                                fontSize: "0.7rem",
                                top: "3px",
                              }}
                            >
                              →
                            </span>
                            {b}
                          </li>
                        ))}
                      </ul>
                      {proj.link && (
                        <a
                          href={proj.link}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display: "inline-flex",
                            marginTop: "16px",
                            padding: "9px 20px",
                            background: "rgba(0,255,136,0.08)",
                            border: "1px solid rgba(0,255,136,0.25)",
                            borderRadius: "6px",
                            color: "#00ff88",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            textDecoration: "none",
                          }}
                        >
                          View Live ↗
                        </a>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                <div
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "0.68rem",
                    color: isOpen ? "#00ff88" : "#6b7889",
                    marginTop: "20px",
                    letterSpacing: "0.05em",
                    transition: "color 0.2s",
                  }}
                >
                  {isOpen ? "Show less" : "Details →"}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ── Side Projects ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          {/* Section divider */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                flex: 1,
                height: "1px",
                background: "rgba(255,255,255,0.05)",
              }}
            />
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "0.6rem",
                color: "#374151",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Also built
            </span>
            <div
              style={{
                flex: 1,
                height: "1px",
                background: "rgba(255,255,255,0.05)",
              }}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "10px",
            }}
          >
            {sideProjectsData.map((proj, i) => (
              <SideProjectCard key={proj.name} proj={proj} index={i} />
            ))}
          </div>
        </motion.div>

        <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@300;400;500&display=swap');
      `}</style>
      </div>
    </ViewWrapper>
  );
};

export default Projects;
